import { apiClient } from './client'
import type { ApiResponse, PageResponse } from '@/types/api'

// ============================================================================
// Types
// ============================================================================

export type AnnouncementStatus = 'draft' | 'published' | 'scheduled' | 'archived'
export type AppType = 'company' | 'lc' | 'b2c'

export interface Announcement {
  announcementId: number
  title: string
  content: string
  contentFormat: string
  status: AnnouncementStatus
  isPinned: boolean
  isGlobal: boolean
  targetAppTypes?: AppType[] | null
  targetOrganizationIds?: number[] | null
  targetAudience?: string | null
  publishStartAt: string | null
  publishEndAt: string | null
  viewCount: number
  authorId: number
  authorName: string
  createdAt: string
  updatedAt: string
}

export interface AnnouncementRequest {
  title: string
  content: string
  status: AnnouncementStatus
  isPinned?: boolean
  isGlobal?: boolean
  targetAppTypes?: AppType[]
  targetOrganizationIds?: number[]
  targetAudience?: string | null
  publishStartDate?: string | null
  publishEndDate?: string | null
}

// ============================================================================
// API Functions
// ============================================================================

// Backend response type (different from standard PageResponse)
export interface AnnouncementListResponse {
  announcements: Announcement[]
  totalCount: number
  page: number
  size: number
  totalPages: number
}

export const announcementApi = {
  getList: async (params: {
    status?: AnnouncementStatus
    appType?: AppType
    page?: number
    size?: number
  } = {}): Promise<AnnouncementListResponse> => {
    const response = await apiClient.get<ApiResponse<AnnouncementListResponse>>(
      '/admin/announcements',
      { params }
    )
    if (!response.data.data) {
      throw new Error('Failed to fetch announcements')
    }
    return response.data.data
  },

  getById: async (id: number): Promise<Announcement> => {
    const response = await apiClient.get<ApiResponse<Announcement>>(
      `/admin/announcements/${id}`
    )
    if (!response.data.data) {
      throw new Error('Announcement not found')
    }
    return response.data.data
  },

  create: async (data: AnnouncementRequest): Promise<Announcement> => {
    const response = await apiClient.post<ApiResponse<Announcement>>(
      '/admin/announcements',
      data
    )
    if (!response.data.data) {
      throw new Error('Failed to create announcement')
    }
    return response.data.data
  },

  update: async (id: number, data: AnnouncementRequest): Promise<Announcement> => {
    const response = await apiClient.put<ApiResponse<Announcement>>(
      `/admin/announcements/${id}`,
      data
    )
    if (!response.data.data) {
      throw new Error('Failed to update announcement')
    }
    return response.data.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/admin/announcements/${id}`)
  },

  publish: async (id: number): Promise<Announcement> => {
    const response = await apiClient.patch<ApiResponse<Announcement>>(
      `/admin/announcements/${id}/publish`
    )
    if (!response.data.data) {
      throw new Error('Failed to publish announcement')
    }
    return response.data.data
  },

  archive: async (id: number): Promise<Announcement> => {
    const response = await apiClient.patch<ApiResponse<Announcement>>(
      `/admin/announcements/${id}/archive`
    )
    if (!response.data.data) {
      throw new Error('Failed to archive announcement')
    }
    return response.data.data
  },
}
